/**
 * Which device and browser the panel is open in, read from what the browser says about itself.
 * Only the install guide uses it: it picks the steps that match what the user sees on screen. The
 * user can always pick another device or browser in the guide, so a wrong guess costs one click.
 */

export type InstallOs = 'ios' | 'ipados' | 'android' | 'windows' | 'macos' | 'linux' | 'chromeos'
export type InstallBrowser =
  | 'safari'
  | 'chrome'
  | 'edge'
  | 'firefox'
  | 'samsung'
  | 'opera'
  | 'yandex'
  | 'brave'
  | 'other'
/** An app that opened the panel in its own built-in browser, where nothing can be installed. */
export type InAppHost = 'instagram' | 'facebook' | 'telegram' | 'whatsapp' | 'line' | 'webview'

export type InstallEnvironment = {
  os: InstallOs | 'other'
  /** The major version of iOS/iPadOS/Android/Safari where the steps depend on it. */
  osVersion?: number
  browser: InstallBrowser
  browserVersion?: number
  inApp?: InAppHost
  mobile: boolean
}

export type EnvironmentInput = {
  userAgent: string
  maxTouchPoints?: number
  /** navigator.userAgentData (Chromium only): the brands name Brave, which its user agent hides. */
  brands?: string[]
}

const major = (match: RegExpMatchArray | null) => {
  const value = match ? Number.parseInt(match[1], 10) : NaN
  return Number.isFinite(value) ? value : undefined
}

const inAppHost = (ua: string, os: InstallEnvironment['os']): InAppHost | undefined => {
  if (/Instagram/i.test(ua)) return 'instagram'
  if (/FBAN|FBAV|FB_IAB|FBIOS/i.test(ua)) return 'facebook'
  if (/Telegram/i.test(ua)) return 'telegram'
  if (/WhatsApp/i.test(ua)) return 'whatsapp'
  if (/\bLine\//.test(ua)) return 'line'
  // Android's WebView marks itself «; wv)»; an iOS app's WebView has no «Safari/» token.
  if (os === 'android' && /;\s*wv\)/.test(ua)) return 'webview'
  if (
    (os === 'ios' || os === 'ipados') &&
    !/Safari\//.test(ua) &&
    !/CriOS|FxiOS|EdgiOS|OPiOS|OPT\//.test(ua)
  )
    return 'webview'
  return undefined
}

export const detectInstallEnvironment = ({
  userAgent,
  maxTouchPoints = 0,
  brands = [],
}: EnvironmentInput): InstallEnvironment => {
  const ua = userAgent || ''
  let os: InstallEnvironment['os'] = 'other'
  let osVersion: number | undefined

  if (/iPhone|iPod/.test(ua)) {
    os = 'ios'
    osVersion = major(ua.match(/OS (\d+)[_.]/))
  } else if (/iPad/.test(ua) || (/Macintosh/.test(ua) && maxTouchPoints > 1)) {
    // iPadOS 13 and later says «Macintosh» like a Mac; only the touch screen tells them apart.
    os = 'ipados'
    osVersion = major(ua.match(/OS (\d+)[_.]/)) ?? major(ua.match(/Version\/(\d+)/))
  } else if (/Android/.test(ua)) {
    os = 'android'
    osVersion = major(ua.match(/Android (\d+)/))
  } else if (/CrOS/.test(ua)) {
    os = 'chromeos'
  } else if (/Windows NT/.test(ua)) {
    os = 'windows'
  } else if (/Macintosh|Mac OS X/.test(ua)) {
    os = 'macos'
  } else if (/Linux|X11/.test(ua)) {
    os = 'linux'
  }

  const apple = os === 'ios' || os === 'ipados'
  let browser: InstallBrowser = 'other'
  let browserVersion: number | undefined
  if (apple) {
    // Every iOS browser is Safari's engine; the name is in its own token.
    if (/CriOS/.test(ua)) browser = 'chrome'
    else if (/EdgiOS/.test(ua)) browser = 'edge'
    else if (/FxiOS/.test(ua)) browser = 'firefox'
    else if (/OPiOS|OPT\//.test(ua)) browser = 'opera'
    else if (/YaBrowser/.test(ua)) browser = 'yandex'
    else if (/Safari\//.test(ua)) browser = 'safari'
  } else if (/SamsungBrowser/.test(ua)) {
    browser = 'samsung'
  } else if (/Edg(e|A)?\//.test(ua)) {
    browser = 'edge'
  } else if (/OPR\/|Opera/.test(ua)) {
    browser = 'opera'
  } else if (/YaBrowser/.test(ua)) {
    browser = 'yandex'
  } else if (/Firefox\//.test(ua)) {
    browser = 'firefox'
  } else if (/Chrome\/|Chromium\//.test(ua)) {
    browser = brands.some((brand) => /Brave/i.test(brand)) ? 'brave' : 'chrome'
  } else if (/Safari\//.test(ua) && /Version\//.test(ua)) {
    browser = 'safari'
    browserVersion = major(ua.match(/Version\/(\d+)/))
  }

  return {
    os,
    osVersion,
    browser,
    browserVersion,
    inApp: inAppHost(ua, os),
    mobile: apple || os === 'android',
  }
}

/** The environment of this tab; safe to call where there is no window (tests, prerendering). */
export const currentInstallEnvironment = (): InstallEnvironment => {
  if (typeof navigator === 'undefined') return {os: 'other', browser: 'other', mobile: false}
  const uaData = (navigator as Navigator & {userAgentData?: {brands?: {brand: string}[]}})
    .userAgentData
  return detectInstallEnvironment({
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
    brands: (uaData?.brands || []).map((item) => item.brand),
  })
}

export const OS_NAMES: Record<InstallEnvironment['os'], string> = {
  ios: 'آیفون',
  ipados: 'آیپد',
  android: 'گوشی یا تبلت اندروید',
  windows: 'رایانهٔ ویندوز',
  macos: 'مک',
  linux: 'رایانهٔ لینوکس',
  chromeos: 'کروم‌بوک',
  other: 'این دستگاه',
}

export const BROWSER_NAMES: Record<InstallBrowser, string> = {
  safari: 'Safari',
  chrome: 'Chrome',
  edge: 'Edge',
  firefox: 'Firefox',
  samsung: 'Samsung Internet',
  opera: 'Opera',
  yandex: 'Yandex',
  brave: 'Brave',
  other: 'مرورگر',
}

export const IN_APP_NAMES: Record<InAppHost, string> = {
  instagram: 'اینستاگرام',
  facebook: 'فیسبوک',
  telegram: 'تلگرام',
  whatsapp: 'واتس‌اپ',
  line: 'لاین',
  webview: 'یک برنامهٔ دیگر',
}
